import { useEffect, useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Crown, UserMinus, Trash2, LogOut, Pencil } from "lucide-react";

interface GroupMember {
  codice_persona: number;
  display_name: string;
  role: 'admin' | 'member';
  joined_at: string;
}

interface RenameGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (groupName: string) => void;
  groupName: string;
  isLoading?: boolean;
}

export function RenameGroupModal({ isOpen, onClose, onConfirm, groupName, isLoading = false }: RenameGroupModalProps) {
  const [newGroupName, setNewGroupName] = useState(groupName);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setNewGroupName(groupName);
      setError("");
    }
  }, [groupName, isOpen]);

  const trimmedGroupName = newGroupName.trim();
  const isUnchanged = trimmedGroupName === groupName;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    if (trimmedGroupName.length < 3) {
      setError("Group name must be at least 3 characters");
      return;
    }

    if (trimmedGroupName.length > 50) {
      setError("Group name must be less than 50 characters");
      return;
    }

    if (isUnchanged) {
      setError("Choose a different group name");
      return;
    }

    setError("");
    onConfirm(trimmedGroupName);
  };

  const handleClose = () => {
    if (!isLoading) {
      setNewGroupName(groupName);
      setError("");
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5" />
            Rename Group
          </DialogTitle>
          <DialogDescription>Update the display name members see for this group.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="renameGroupName">Group Name</Label>
            <Input
              id="renameGroupName"
              value={newGroupName}
              onChange={(event) => {
                setNewGroupName(event.target.value);
                setError("");
              }}
              maxLength={50}
              disabled={isLoading}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">{newGroupName.length}/50 characters</p>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading} className="flex-1">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || trimmedGroupName.length < 3 || isUnchanged}
              className="flex-1"
            >
              {isLoading ? "Renaming..." : "Rename Group"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface KickMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (memberCodicePersona: number) => void;
  member: GroupMember | null;
  isLoading?: boolean;
}

export function KickMemberModal({ isOpen, onClose, onConfirm, member, isLoading = false }: KickMemberModalProps) {
  const handleConfirm = () => {
    if (member) {
      onConfirm(member.codice_persona);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <UserMinus className="h-5 w-5" />
            Kick Member
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to remove this member from the group?
          </DialogDescription>
        </DialogHeader>

        {member && (
          <div className="space-y-4">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                This action cannot be undone. The member will lose access to the group and all group features.
              </AlertDescription>
            </Alert>

            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="font-medium">{member.display_name}</p>
              <p className="text-sm text-muted-foreground">Codice: {member.codice_persona}</p>
              <p className="text-sm text-muted-foreground">Member since: {new Date(member.joined_at).toLocaleDateString()}</p>
            </div>

            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={onClose} disabled={isLoading} className="flex-1">
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirm}
                disabled={isLoading}
                className="flex-1"
              >
                {isLoading ? "Kicking..." : "Kick Member"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface TransferAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (newAdminCodicePersona: number) => void;
  member: GroupMember | null;
  currentAdminName: string;
  isLoading?: boolean;
}

export function TransferAdminModal({ isOpen, onClose, onConfirm, member, currentAdminName, isLoading = false }: TransferAdminModalProps) {
  const [confirmationText, setConfirmationText] = useState("");
  const [error, setError] = useState("");

  const handleConfirm = () => {
    if (!member) return;

    if (confirmationText !== "TRANSFER") {
      setError('Please type "TRANSFER" to confirm');
      return;
    }

    setError("");
    onConfirm(member.codice_persona);
  };

  const handleClose = () => {
    setConfirmationText("");
    setError("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5" />
            Transfer Admin Role
          </DialogTitle>
          <DialogDescription>
            Transfer admin privileges to another group member
          </DialogDescription>
        </DialogHeader>

        {member && (
          <div className="space-y-4">
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                You will lose admin privileges and become a regular member. This action cannot be undone.
              </AlertDescription>
            </Alert>

            <div className="space-y-3">
              <div>
                <Label className="text-sm font-medium">Current Admin</Label>
                <div className="bg-muted/50 p-3 rounded-lg mt-1">
                  <p className="font-medium">{currentAdminName}</p>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">New Admin</Label>
                <div className="bg-primary/10 border border-primary/20 p-3 rounded-lg mt-1">
                  <p className="font-medium">{member.display_name}</p>
                  <p className="text-sm text-muted-foreground">Codice: {member.codice_persona}</p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmation">Type "TRANSFER" to confirm</Label>
              <Input
                id="confirmation"
                value={confirmationText}
                onChange={(e) => {
                  setConfirmationText(e.target.value);
                  setError("");
                }}
                placeholder="TRANSFER"
                className="font-mono"
              />
              {error && <p className="text-sm text-destructive">{error}</p>}
            </div>

            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={handleClose} disabled={isLoading} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={isLoading || confirmationText !== "TRANSFER"}
                className="flex-1"
              >
                {isLoading ? "Transferring..." : "Transfer Admin Role"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface DisbandGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  groupName: string;
  isLoading?: boolean;
}

export function DisbandGroupModal({ isOpen, onClose, onConfirm, groupName, isLoading = false }: DisbandGroupModalProps) {
  const [confirmationText, setConfirmationText] = useState("");
  const [error, setError] = useState("");

  const handleConfirm = () => {
    if (confirmationText !== groupName) {
      setError(`Please type "${groupName}" to confirm`);
      return;
    }

    setError("");
    onConfirm();
  };

  const handleClose = () => {
    setConfirmationText("");
    setError("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-5 w-5" />
            Disband Group
          </DialogTitle>
          <DialogDescription>
            Permanently delete this group and all associated data
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="space-y-2">
              <p><strong>Warning:</strong> This action cannot be undone and will permanently delete:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>The group "{groupName}"</li>
                <li>All member access</li>
                <li>All invite codes</li>
                <li>All group data and history</li>
              </ul>
              <p>All members will be redirected to the dashboard.</p>
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label htmlFor="confirmation">Type group name to confirm:</Label>
            <Input
              id="confirmation"
              value={confirmationText}
              onChange={(e) => {
                setConfirmationText(e.target.value);
                setError("");
              }}
              placeholder={groupName}
              className="font-mono"
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={handleClose} disabled={isLoading} className="flex-1">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={isLoading || confirmationText !== groupName}
              className="flex-1"
            >
              {isLoading ? "Disbanding..." : "Disband Group"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface LeaveGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  groupName: string;
  isLoading?: boolean;
}

export function LeaveGroupModal({ isOpen, onClose, onConfirm, groupName, isLoading = false }: LeaveGroupModalProps) {
  const [confirmationText, setConfirmationText] = useState("");
  const [error, setError] = useState("");

  const handleConfirm = () => {
    if (confirmationText !== "LEAVE") {
      setError('Please type "LEAVE" to confirm');
      return;
    }

    setError("");
    onConfirm();
  };

  const handleClose = () => {
    setConfirmationText("");
    setError("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LogOut className="h-5 w-5" />
            Leave Group
          </DialogTitle>
          <DialogDescription>
            Leave this group and lose access to all group features
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="space-y-2">
              <p><strong>Important:</strong> This action will:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Remove you from the group "{groupName}"</li>
                <li>Revoke your access to group features</li>
                <li>Prevent you from generating tokens for this group</li>
              </ul>
              <p>You will need an invite code to rejoin this group.</p>
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label htmlFor="confirmation">Type "LEAVE" to confirm:</Label>
            <Input
              id="confirmation"
              value={confirmationText}
              onChange={(e) => {
                setConfirmationText(e.target.value);
                setError("");
              }}
              placeholder="LEAVE"
              className="font-mono"
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={handleClose} disabled={isLoading} className="flex-1">
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleConfirm}
              disabled={isLoading || confirmationText !== "LEAVE"}
              className="flex-1"
            >
              {isLoading ? "Leaving..." : "Leave Group"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}